// =====================================================================================
// INVRT Radar - web console - the HIGH-INTENT campaign detail (story P2-UI, campaign first)
//
// One detail route per bound campaign (/high-intent/campaigns/{code}), one job per tab:
//   Overview  the Campaign Reference content - overview, strategy brief, the search-term
//             list, the settings table and the volume on record - READ-ONLY and labelled
//             "From the Campaign Reference". The only mutable field is the campaign's
//             catalogue status, edited through the audited E03 PATCH (status + reason,
//             with the row's expected version) and offered to super-admins only; the
//             server, not this screen, decides authority.
//   Runs      the proxy's runs read for this campaign (no picker, no start control).
//   Reports   HOW THE DATA WAS PROCESSED, from the engine's row ledger and
//             scored leads: every row accounted for by disposition, every
//             refusal with its WHY, the Tag A/B/C spread, warm/standard, and
//             re-intent split by the counted law - beside the spend days and
//             the reference volume. All of it is what the stats contract
//             returns; nothing is invented here.
//   Delivery  the organisation's delivery selection for this campaign: preview,
//             draft save (SAVE IS NOT SEND - draft persistence only) and the
//             server's readiness truth. See DeliverySelectionPanel.tsx.
//
// A campaign with no reference entry renders an honest empty state naming the gap - never
// borrowed content, never fabricated figures.
// =====================================================================================

import { useMemo, useState, type FormEvent } from "react";
import { Link, NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/chip";
import { Dialog } from "@/components/ui/dialog";
import { Select, Textarea } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { useSession } from "@/auth/SessionProvider";
import { useApiClient } from "@/hooks/useApi";
import { isApiError } from "@/lib/api";
import {
  CAMPAIGN_REFERENCE_TAG_LEGEND,
  campaignReferenceForCode,
  normaliseCampaignKey,
} from "@/data/campaignReference";
import {
  parseMutationResponse,
  readCatalogueKind,
} from "@/pages/operator/catalogueReader";
import { cn } from "@/lib/utils";
import { CampaignRuns } from "./AnalysisRuns";
import { useRadarCampaigns, useRadarCampaignStats } from "./useRadar";
import * as DeliverySelectionPanel from "./DeliverySelectionPanel";
import * as UploadPanel from "./UploadPanel";
import { AudienceTab } from "./AudienceTab";
import type { RadarCampaign, RadarStats } from "@/lib/types.radar";

const TABS = [
  { to: "audience", label: "Audience", end: false },
  { to: "", label: "Overview", end: true },
  { to: "runs", label: "Runs", end: false },
  { to: "reports", label: "Reports", end: false },
  { to: "delivery", label: "Delivery", end: false },
  { to: "import", label: "Import", end: false },
];

/**
 * Resolves the bound campaign from the route's campaignKey: a proxy code (hyphenated) or
 * a raw id, matched against the organisation's bound campaigns.
 */
function useCampaignFromRoute(): {
  campaignKey: string;
  campaignsLoading: boolean;
  campaignsError: unknown;
  campaignsRefetch: () => void;
  campaign: RadarCampaign | undefined;
} {
  const { campaignKey = "" } = useParams();
  const campaigns = useRadarCampaigns();

  const campaign = useMemo(() => {
    const rows = campaigns.data?.campaigns ?? [];
    const wanted = normaliseCampaignKey(campaignKey);
    return rows.find(
      (row) =>
        (row.code !== null && normaliseCampaignKey(row.code) === wanted) || row.id === campaignKey,
    );
  }, [campaigns.data, campaignKey]);

  return {
    campaignKey,
    campaignsLoading: campaigns.isLoading,
    campaignsError: campaigns.error,
    campaignsRefetch: () => void campaigns.refetch(),
    campaign,
  };
}

// ---------------------------------------------------------------------------------------
// The reference panel: everything here is the document's own words and figures.
// ---------------------------------------------------------------------------------------

function ReferencePanel({ reference }: { reference: NonNullable<ReturnType<typeof campaignReferenceForCode>> }) {
  return (
    <div className="flex flex-col gap-5" data-testid="campaign-reference">
      <h2 className="text-lg font-semibold text-ink">From the Campaign Reference</h2>

      <div className="inv-card flex flex-col gap-2 p-5" data-pad>
        <h3 className="inv-label">Overview (as stored)</h3>
        <blockquote className="border-l-2 border-line pl-3 text-sm text-ink-2">{reference.overview}</blockquote>
      </div>

      <div className="inv-card flex flex-col gap-2 p-5" data-pad>
        <h3 className="inv-label">Strategy / brief (as stored)</h3>
        <p className="whitespace-pre-line text-sm text-ink-2">{reference.strategy}</p>
      </div>

      <div className="inv-card flex flex-col gap-2 p-5" data-pad>
        <h3 className="inv-label">High-intent search terms ({reference.searchTerms.length})</h3>
        <ul className="flex flex-wrap gap-2">
          {reference.searchTerms.map((term) => (
            <li key={term} className="border border-line bg-surface-muted px-2 py-0.5 text-xs text-ink">
              {term}
            </li>
          ))}
        </ul>
      </div>

      <div className="inv-card flex flex-col gap-2 p-5" data-pad>
        <h3 className="inv-label">Settings</h3>
        <Table aria-label="Campaign settings from the reference">
          <TableHead>
            <TableRow>
              <TableHeaderCell>Setting</TableHeaderCell>
              <TableHeaderCell>Value</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reference.settings.map((row) => (
              <TableRow key={row.label}>
                <TableCell className="font-medium">{row.label}</TableCell>
                <TableCell>{row.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="inv-card flex flex-col gap-2 p-5" data-pad>
        <h3 className="inv-label">Volume on record</h3>
        <p className="text-sm text-ink-2">{reference.volume.sentence}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// The audited status edit: status + reason, through the E03 catalogue PATCH.
// ---------------------------------------------------------------------------------------

function CampaignStatusDialog({
  open,
  campaignCode,
  campaignStatus,
  onClose,
}: {
  open: boolean;
  campaignCode: string;
  campaignStatus: string;
  onClose: () => void;
}) {
  const api = useApiClient();
  const { push } = useToast();
  const wanted = normaliseCampaignKey(campaignCode);
  const [status, setStatus] = useState<"active" | "inactive">(
    campaignStatus === "inactive" ? "inactive" : "active",
  );
  const [reason, setReason] = useState("");
  const [refusal, setRefusal] = useState("");
  const [failure, setFailure] = useState("");
  const [pending, setPending] = useState(false);

  // The audited PATCH points at a catalogue row; the walk happens once, on demand,
  // exactly as the assignment dialog's choosers do.
  const catalogue = useQuery({
    queryKey: ["high-intent", "campaign-detail", "catalogue-campaigns"],
    enabled: open,
    queryFn: () => readCatalogueKind(api, "campaign"),
    staleTime: 30_000,
  });

  const row = useMemo(() => {
    const items = catalogue.data ?? [];
    return items.find((item) => normaliseCampaignKey(item.code) === wanted);
  }, [catalogue.data, wanted]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = reason.trim();
    if (trimmed.length < 3 || trimmed.length > 500) {
      setRefusal("Provide a reason between 3 and 500 characters. No change was made.");
      return;
    }
    if (!row) {
      setRefusal("No catalogue row to point at. No change was made.");
      return;
    }
    setRefusal("");
    setFailure("");
    setPending(true);
    try {
      const response = await api.patch<unknown>(
        `/catalogue/campaign/${row.id}`,
        { status, expected_version: row.version, reason: trimmed },
        { requiresOrg: false, includeOrg: false },
      );
      if (parseMutationResponse(response, "campaign") === null) {
        setFailure("The server's response did not match the reviewed contract. No change was made.");
        return;
      }
      push({ tone: "success", title: "Campaign status saved" });
      onClose();
    } catch (caught) {
      if (isApiError(caught) && caught.httpStatus === 403) {
        setFailure("You do not have permission to edit this campaign. No change was made.");
      } else {
        setFailure("The status was not saved. No change was made. Try again.");
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Edit campaign status"
      description="The audited edit scope for a campaign is its status and a recorded reason. Everything else on this campaign is read-only reference."
      footer={(
        <>
          <Button type="button" variant="secondary" className="min-h-11" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" form="campaign-status-form" variant="primary" className="min-h-11" loading={pending}>
            Save status
          </Button>
        </>
      )}
    >
      {catalogue.isLoading ? <LoadingState label="Loading the campaign catalogue…" /> : null}
      {catalogue.isError && isApiError(catalogue.error) && catalogue.error.httpStatus === 403 ? (
        <EmptyState
          title="You do not have permission to edit this campaign."
          body="The server refused this operator request."
        />
      ) : null}
      {catalogue.isError && !(isApiError(catalogue.error) && catalogue.error.httpStatus === 403) ? (
        <ErrorState error={catalogue.error} onRetry={() => void catalogue.refetch()} />
      ) : null}
      {catalogue.isSuccess && !row ? (
        <EmptyState
          title={`No catalogue campaign matches ${campaignCode}`}
          body="The audited status edit works through the campaign catalogue, and no catalogue row carries this campaign's code. Nothing else about a campaign is editable here."
        />
      ) : null}
      {catalogue.isSuccess && row ? (
        <form id="campaign-status-form" noValidate className="flex flex-col gap-4" onSubmit={submit}>
          <p className="text-sm text-ink-2">
            {row.name} ({row.code}) · catalogue version {row.version}
          </p>
          <Select
            label="Status"
            value={status}
            onChange={(event) => setStatus(event.target.value as "active" | "inactive")}
            disabled={pending}
          >
            <option value="active">active</option>
            <option value="inactive">inactive</option>
          </Select>
          <Textarea
            label="Reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            minLength={3}
            maxLength={500}
            disabled={pending}
            hint="Required for the server audit record."
          />
          {refusal || failure ? (
            <p role="alert" aria-live="assertive" className="text-sm text-danger">
              {refusal || failure}
            </p>
          ) : null}
        </form>
      ) : null}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------
// The tabs
// ---------------------------------------------------------------------------------------

export function CampaignOverviewTab() {
  const { campaign } = useCampaignFromRoute();
  const { isSuperAdmin } = useSession();
  const [editOpen, setEditOpen] = useState(false);

  if (!campaign) return null; // the shell renders the not-bound state

  const reference = campaignReferenceForCode(campaign.code);
  const code = campaign.code ?? campaign.id;

  return (
    <div className="flex flex-col gap-6">
      {isSuperAdmin ? (
        <div>
          <Button
            type="button"
            variant="secondary"
            className="min-h-11"
            onClick={() => setEditOpen(true)}
          >
            Edit campaign status
          </Button>
          <p className="mt-1 text-xs text-ink-3">
            The audited edit scope is the campaign's status and a recorded reason, through the
            campaign catalogue. Everything else is reference.
          </p>
        </div>
      ) : null}

      {reference ? (
        <ReferencePanel reference={reference} />
      ) : (
        <EmptyState
          title={`No Campaign Reference entry for ${code}`}
          body={`The campaign is bound and runs, but Mark's reference document carries no entry for its key (${normaliseCampaignKey(code)}). Nothing is invented here: add the entry to the reference and regenerate.`}
        />
      )}

      {editOpen ? (
        <CampaignStatusDialog
          open
          campaignCode={code}
          campaignStatus={campaign.status}
          onClose={() => setEditOpen(false)}
        />
      ) : null}
    </div>
  );
}

export function CampaignRunsTab() {
  const { campaign } = useCampaignFromRoute();
  if (!campaign) return null;
  return <CampaignRuns campaignId={campaign.id} />;
}

function formatDailyUsd(value: string | null): string {
  if (value === null || value.trim() === "" || !Number.isFinite(Number(value))) return "Not available";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(Number(value));
}

function StatsBlock({ stats }: { stats: RadarStats }) {
  if (stats.spend_by_day.length === 0) {
    return <p className="text-sm text-ink-2">No spend recorded for this campaign yet.</p>;
  }
  return (
    <Table aria-label="Spend by day">
      <TableHead>
        <TableRow>
          <TableHeaderCell>Day</TableHeaderCell>
          <TableHeaderCell className="text-right">Calls</TableHeaderCell>
          <TableHeaderCell className="text-right">Estimated (USD)</TableHeaderCell>
          <TableHeaderCell className="text-right">Actual (USD)</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {stats.spend_by_day.map((row) => (
          <TableRow key={row.call_date}>
            <TableCell className="font-mono text-xs">{row.call_date}</TableCell>
            <TableCell className="text-right font-mono tabular-nums">{row.calls}</TableCell>
            <TableCell className="text-right font-mono tabular-nums">{formatDailyUsd(row.estimated_cost_usd)}</TableCell>
            <TableCell className="text-right font-mono tabular-nums">
              {formatDailyUsd(row.actual_cost_usd)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** The ledger's refusal reasons in the operator's words; the raw taxonomy
 * token stays beside it so the WHY is never paraphrased away. */
const REASON_LABELS: Record<string, string> = {
  quality_score_below_threshold: "Excluded by the import-time quality rule",
  column_count_mismatch: "Wrong number of columns in the row",
  unparseable_row: "Row could not be read",
  encoding_error: "Encoding error in the row",
  no_identity: "No readable person identity in the row",
  malformed_uuid: "Unreadable provider id in the row",
  window_overlap: "Repeat inside an overlapping batch window (not counted)",
  same_batch: "Repeat inside the same batch (not counted)",
};

function reasonLabel(reason: string): string {
  return REASON_LABELS[reason] ?? reason;
}

function ProcessingBlock({ stats }: { stats: RadarStats }) {
  const processing = stats.processing;
  if (processing === null) {
    return (
      <p className="text-sm text-ink-2">
        The engine has not answered with the processing breakdown yet (an older engine
        version). Nothing is invented here.
      </p>
    );
  }
  const dispositions = Object.entries(processing.dispositions);
  const tags = Object.entries(processing.tag_counts).sort();
  const leadTags = Object.entries(processing.lead_tag_counts).sort();
  return (
    <div className="flex flex-col gap-4" data-testid="processing-breakdown">
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3" aria-label="Import and intent counts">
        <div><dt className="inv-label">Source records processed</dt><dd className="inv-mono mt-2 text-xl text-ink">{processing.parsed_records.toLocaleString("en-GB")}</dd></div>
        <div><dt className="inv-label">New leads accepted</dt><dd className="inv-mono mt-2 text-xl text-ink">{stats.campaign?.new_leads.toLocaleString("en-GB") ?? "Not available"}</dd></div>
        <div><dt className="inv-label">Intent threshold met</dt><dd className="inv-mono mt-2 text-xl text-ink">{stats.campaign?.high_intent_count.toLocaleString("en-GB") ?? "Not available"}</dd></div>
      </dl>
      <p className="text-sm leading-relaxed text-ink-2">
        The intent threshold and Tag A/B/C are separate source scoring measures.
        Your organisation's delivery selection is a separate step; these counts do not show
        how many leads were selected or delivered.
        The row breakdown below also accounts for re-intent events and rejected records.
      </p>
      <div className="flex flex-wrap gap-2">
        <Chip signal="ctx">Batches {processing.batches}</Chip>
        <Chip signal="ctx">Parsed {processing.parsed_records.toLocaleString()}</Chip>
        <Chip signal="ctx">
          Re-intent counted {processing.re_intent_counted.toLocaleString()}
        </Chip>
        <Chip signal="ctx">
          Re-deliveries (never counted) {processing.re_intent_not_counted.toLocaleString()}
        </Chip>
        {tags.map(([tag, count]) => (
          <Chip key={tag} signal="ctx">
            Tag {tag}: {count.toLocaleString()}
          </Chip>
        ))}
        {leadTags.map(([tag, count]) => (
          <Chip key={tag} signal="ctx">
            {tag}: {count.toLocaleString()}
          </Chip>
        ))}
      </div>

      <div>
        <h3 className="inv-label">Every row, accounted for</h3>
        {dispositions.length > 0 ? (
          <Table aria-label="Rows by disposition">
            <TableHead>
              <TableRow>
                <TableHeaderCell>Disposition</TableHeaderCell>
                <TableHeaderCell className="text-right">Rows</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dispositions.map(([disposition, count]) => (
                <TableRow key={disposition}>
                  <TableCell className="font-medium">{disposition}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">
                    {count.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-sm text-ink-2">No rows ingested yet.</p>
        )}
      </div>

      <div>
        <h3 className="inv-label">Refused, and why</h3>
        {processing.rejection_reasons.length > 0 ? (
          <Table aria-label="Refusal reasons">
            <TableHead>
              <TableRow>
                <TableHeaderCell>Why</TableHeaderCell>
                <TableHeaderCell>Reason (engine taxonomy)</TableHeaderCell>
                <TableHeaderCell className="text-right">Rows</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processing.rejection_reasons.map((row) => (
                <TableRow key={row.reason}>
                  <TableCell className="font-medium">{reasonLabel(row.reason)}</TableCell>
                  <TableCell className="font-mono text-xs text-ink-2">{row.reason}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">
                    {row.count.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-sm text-ink-2">No rows refused.</p>
        )}
      </div>
    </div>
  );
}

export function CampaignReportsTab() {
  const { campaign } = useCampaignFromRoute();
  const stats = useRadarCampaignStats(campaign?.id ?? "");
  const denied = stats.isError && isApiError(stats.error) && stats.error.httpStatus === 403;
  if (!campaign) return null;
  const reference = campaignReferenceForCode(campaign.code);
  const code = campaign.code ?? campaign.id;

  return (
    <div className="flex flex-col gap-6">
      <div className="inv-card flex flex-col gap-3 p-5" data-pad>
        <h2 className="text-base font-semibold text-ink">How the data was processed (live, from the engine)</h2>
        <p className="text-sm text-ink-2">
          Every ingested row accounted for, every refusal with its reason, and the scored
          outcome - straight from the engine's row ledger and leads. Re-deliveries are shown
          but never counted.
        </p>
        {stats.isLoading ? <LoadingState label="Loading the processing breakdown…" /> : null}
        {stats.isError && !denied ? (
          <ErrorState error={stats.error} onRetry={() => void stats.refetch()} />
        ) : null}
        {denied ? (
          <EmptyState
            title="You do not have permission to view these stats."
            body="The server refused this request for the selected organisation."
          />
        ) : null}
        {stats.isSuccess ? <ProcessingBlock stats={stats.data} /> : null}
      </div>

      <div className="inv-card flex flex-col gap-2 p-5" data-pad>
        <h2 className="text-base font-semibold text-ink">Spend (live, from the engine)</h2>
        {stats.isLoading ? <LoadingState label="Loading stats…" /> : null}
        {stats.isError && !denied ? (
          <ErrorState error={stats.error} onRetry={() => void stats.refetch()} />
        ) : null}
        {denied ? (
          <EmptyState
            title="You do not have permission to view these stats."
            body="The server refused this request for the selected organisation."
          />
        ) : null}
        {stats.isSuccess ? <StatsBlock stats={stats.data} /> : null}
      </div>

      {reference ? (
        <div className="inv-card flex flex-col gap-2 p-5" data-pad>
          <h2 className="text-base font-semibold text-ink">From the Campaign Reference</h2>
          <p className="text-sm text-ink-2">{reference.volume.sentence}</p>
          <p className="text-sm text-ink-2">{CAMPAIGN_REFERENCE_TAG_LEGEND}</p>
          <p className="text-xs text-ink-3">
            The live figures above are the engine's own account of the ingested data; the
            volumes here are the reference document's on-record expectations. Where they
            differ, both are shown and neither is edited to match the other.
          </p>
        </div>
      ) : (
        <EmptyState
          title={`No Campaign Reference entry for ${code}`}
          body="The live figures above are what the engine returns; there is no reference volume to set beside it."
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// The delivery tab (story H07): the org selection, preview and readiness truth. The
// campaign's HIGH-INTENT key (its code) is what the org delivery routes address; the
// normalised form is only for route matching and display.
// ---------------------------------------------------------------------------------------

export function CampaignAudienceTab() {
  const { campaign } = useCampaignFromRoute();
  return campaign ? <AudienceTab campaignId={campaign.id} campaignKey={campaign.code} /> : null;
}

export function CampaignDeliveryTab() {
  const { campaign } = useCampaignFromRoute();
  const campaignKey = campaign?.code ?? "";
  if (!campaign || campaignKey === "") return null; // the shell renders the not-bound state
  return <DeliverySelectionPanel.CampaignDeliveryTab campaignKey={campaignKey} />;
}

// ---------------------------------------------------------------------------------------
// The import tab (story O05): the manual CSV upload through the engine's existing
// trusted-source machinery. The campaign's HIGH-INTENT key is what the ingest route
// addresses, exactly as the delivery routes do.
// ---------------------------------------------------------------------------------------

export function CampaignImportTab() {
  const { campaign } = useCampaignFromRoute();
  const campaignKey = campaign?.code ?? "";
  if (!campaign || campaignKey === "") return null; // the shell renders the not-bound state
  return <UploadPanel.CampaignImportTab campaignKey={campaignKey} />;
}

// ---------------------------------------------------------------------------------------
// The detail shell
// ---------------------------------------------------------------------------------------

export function CampaignDetailPage() {
  const { campaignKey, campaignsLoading, campaignsError, campaignsRefetch, campaign } =
    useCampaignFromRoute();
  const navigate = useNavigate();

  if (campaignsLoading) {
    return <LoadingState label="Loading campaigns…" />;
  }
  if (campaignsError) {
    return <ErrorState error={campaignsError} onRetry={campaignsRefetch} />;
  }
  if (!campaign) {
    return (
      <EmptyState
        title={`No bound campaign matches ${campaignKey}`}
        body="This campaign is not bound to your organisation's HIGH-INTENT subscription. Choose a campaign from the board."
        action={(
          <Button type="button" variant="secondary" className="min-h-11" onClick={() => void navigate("/high-intent")}>
            Back to campaigns
          </Button>
        )}
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <Link
          to="/high-intent"
          className="inline-flex min-h-11 items-center gap-2 text-sm text-primary underline-offset-2 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          All campaigns
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="type-app-h text-xl font-semibold text-ink">
            {campaign.name ?? campaign.code ?? campaign.id}
          </h1>
          <Chip signal={campaign.status === "active" ? "buy" : undefined} dot={campaign.status === "active"}>
            {campaign.status}
          </Chip>
          {campaign.code ? (
            <span className="inv-mono text-xs text-ink-3">{normaliseCampaignKey(campaign.code)}</span>
          ) : null}
        </div>
      </div>

      <nav aria-label="Campaign sections" className="flex gap-1 overflow-x-auto border-b border-line">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              cn(
                "-mb-px inline-flex min-h-11 items-center whitespace-nowrap border-b-2 px-4 text-sm font-medium transition-colors",
                isActive
                  ? "border-primary text-primary"
                  : "border-transparent text-ink-muted hover:border-line hover:text-ink",
              )
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <Outlet />
    </div>
  );
}
